import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Users, MapPin, Globe, ArrowRight, ShoppingBag } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function PublicCreatorsPage() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/public/creators');
        setCreators(res.data.creators || []);
      } catch {
        // fallback
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'PushPro Creators',
    description: 'Discover talented creators selling premium digital products on PushPro.',
    url: `${window.location.origin}/creators`,
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="Creators"
        description="Browse talented creators on PushPro. Find digital artists, designers, and content creators selling premium products."
        url={`${window.location.origin}/creators`}
        schema={schema}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
          <Link to="/" className="font-serif text-lg tracking-tight">
            <span className="text-obsidian">Push</span>
            <span className="text-amber-600 font-bold">Pro</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/marketplace">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex">Marketplace</Button>
            </Link>
            <Link to="/auth">
              <Button className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-amber-600 transition-colors duration-300">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200/50 px-4 py-2 rounded-full mb-6">
            <Users size={14} className="text-amber-600" />
            <span className="text-xs font-medium text-amber-800 uppercase tracking-wider">Creator Directory</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-obsidian mb-4" data-testid="creators-page-title">
            Discover Creators
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto font-light">
            Talented creators building and selling premium digital products on PushPro.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
          </div>
        ) : creators.length === 0 ? (
          <div className="text-center py-16">
            <Users size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No creators yet. Be the first!</p>
            <Link to="/auth">
              <Button className="mt-4 rounded-full bg-obsidian hover:bg-amber-600">Join as Creator</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="creators-grid">
            {creators.map((creator) => (
              <Link
                key={creator.id}
                to={`/profile/${creator.id}`}
                className="group bg-white border border-border/40 rounded-2xl p-6 hover:border-amber-300/50 hover:shadow-xl hover:shadow-amber-100/30 transition-all duration-500"
                data-testid={`creator-card-${creator.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <Avatar className="h-14 w-14 border-2 border-amber-100 group-hover:border-amber-300 transition-colors">
                    <AvatarFallback className="bg-gradient-to-br from-amber-100 to-rose-100 text-amber-700 font-serif text-lg">
                      {(creator.display_name || creator.username || 'C')[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg group-hover:text-amber-700 transition-colors truncate">
                      {creator.display_name || creator.username}
                    </h3>
                    <p className="text-xs text-muted-foreground">@{creator.username}</p>
                  </div>
                </div>

                {creator.bio && (
                  <p className="text-sm text-muted-foreground font-light line-clamp-2 mb-4">{creator.bio}</p>
                )}

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {creator.country && (
                    <span className="flex items-center gap-1">
                      <MapPin size={12} /> {creator.country}
                    </span>
                  )}
                  {creator.product_count > 0 && (
                    <span className="flex items-center gap-1">
                      <ShoppingBag size={12} /> {creator.product_count} products
                    </span>
                  )}
                  {creator.website && (
                    <span className="flex items-center gap-1">
                      <Globe size={12} /> Website
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-border/30 flex items-center justify-between">
                  <span className="text-xs text-amber-600 font-medium group-hover:text-amber-700">View Profile</span>
                  <ArrowRight size={14} className="text-amber-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">PushPro. Premium creator marketplace.</p>
          <div className="flex items-center gap-6">
            <Link to="/marketplace" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Marketplace</Link>
            <Link to="/creators" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Creators</Link>
            <Link to="/auth" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
